import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import type { ChangeEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import PageLoader from "components/common/PageLoader";
import TalentCard from "components/Screens/Marketplace/Customer/TalentNetwork/TalentCard";
import FilterDropdown from "components/Screens/Marketplace/Customer/TalentNetwork/FilterDropdown";
import InterviewSuccessModal from "components/Screens/Marketplace/Customer/TalentNetwork/InterviewSuccessModal";
import { hireNudgeAfter, parseNudgePositions } from "components/Screens/Marketplace/Customer/TalentNetwork/hireNudge";
import PlgIcon from "plg/shell/PlgIcon";
import HireNudgeRow from "components/Screens/Marketplace/Customer/TalentNetwork/HireNudgeRow";
import MobileFilterSheet from "components/Screens/Marketplace/Customer/TalentNetwork/MobileFilterSheet";
import useTalentFilterOptions, { withPickedTools } from "components/Screens/Marketplace/Customer/TalentNetwork/useTalentFilterOptions";
import { toolKey, uniqueTools } from "components/Screens/Marketplace/Customer/utils/tools";
import { fetchCompanyTools } from "plg/api/plgCompany";
import useCustomerTools from "components/Screens/Marketplace/Customer/utils/useCustomerTools";
import { loadListState, saveListState } from "components/Screens/Marketplace/Customer/TalentNetwork/talentListState";

import { analytics } from "services/analytics";

import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import usePlgSelfServe from "hooks/usePlgSelfServe";
import { useMeeting } from "plg/meeting/MeetingContext";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";

import SearchIcon from "@mui/icons-material/Search";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RefreshIcon from "@mui/icons-material/Refresh";

import type { TalentListItem, TalentListPagination } from "components/Screens/Marketplace/Customer/types";

import getTalentFullName from "components/Screens/Marketplace/Customer/utils/talentName";

import LANGUAGES from "constants/language";
import usePlgFilterTracking from "plg/analytics/usePlgFilterTracking";
import { rememberListContext } from "plg/analytics/plgAnalyticsSession";
import { rememberOpenedFilters, setListFilters } from "components/Screens/Marketplace/Customer/Shortlist/shortlistFilters";

import "./TalentNetworkPage.scss";

const TOP_LANGUAGES = [
  { value: "English", label: "English" },
  { value: "Spanish", label: "Spanish" },
];

const ALL_LANGUAGES = LANGUAGES.slice(2).map((lang) => ({ value: lang, label: lang }));

const ROLE_HELPER = "Describes what the person does every day.";
const LANGUAGES_HELPER =
  "Additional languages can help unlock access to new patient/customer segments. We show frequently selected ones at the top.";

const ROLE_TAUGHT_KEY = "tn-role-taught";

function TalentNetworkPage() {
  const navigate = useNavigate();
  const [talents, setTalents] = useState<TalentListItem[]>([]);
  const [pagination, setPagination] = useState<TalentListPagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Back from a profile: the list as it was left (filters, page, scroll).
  const [restored] = useState(loadListState);
  const restoreScroll = useRef(restored?.scrollY ?? 0);
  const [currentPage, setCurrentPage] = useState(restored?.page ?? 1);
  const [itemsPerPage, setItemsPerPage] = useState(restored?.perPage ?? 5);

  // Multi-select filters (arrays)
  const [roleFilters, setRoleFilters] = useState<string[]>(restored?.roles ?? []);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [toolFilters, setToolFilters] = useState<string[]>(restored?.tools ?? []);
  const [languageFilters, setLanguageFilters] = useState<string[]>(restored?.languages ?? []);


  const [hasError, setHasError] = useState(false);

  // Interview request state
  const [requestingTalentId, setRequestingTalentId] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const api = useTalentApi();
  // PLG, first visit in this tab: the list opens filtered on the apps the customer gave at sign-up (or on the
  // Company page), so it delivers on asking for them. The first fetch waits for them. Back from a profile, or
  // once the customer has changed the filters, the saved list state is used instead.
  const [seedingTools, setSeedingTools] = useState(() => api.kind === "plg" && !restored);
  const toolsSeeded = useRef(false);
  const { talentNetworkVisible: talentNetworkVisibility, userInfoLoading: loadingUserInfo, user: loggedInUser } = api;

  const canRequestHire = api.hasPermission("talentNetwork.request_hire");
  // PLG: one CTA everywhere, Request a meeting (side panel). Portal customers keep Request a hire.
  const meeting = useMeeting();
  const { count: shortlisted } = useShortlist();
  const shortlistMode = usePlgSelfServe();
  const nudgePositions = parseNudgePositions(
    useSelector((state: any) => state.PlatformConfig?.platformConfig?.payload?.TALENT_NETWORK_HIRE_NUDGE_AFTER)
  );
  const [roleTaught, setRoleTaught] = useState(() => {
    try {
      return sessionStorage.getItem(ROLE_TAUGHT_KEY) === "1";
    } catch {
      return false;
    }
  });

  usePlgFilterTracking(
    api.kind === "plg",
    { role: roleFilters, tools: toolFilters, language: languageFilters },
    isLoading,
    hasError ? null : pagination?.total ?? talents.length,
    seedingTools
  );

  const { roleOptions, toolOptions: poolToolOptions } = useTalentFilterOptions(api);
  const myTools = useCustomerTools();
  // The customer's own apps are listed (and so can be unticked) even when no talent lists them yet.
  const toolOptions = withPickedTools(poolToolOptions, [...myTools, ...toolFilters]);
  // Their onboarding apps stay at the top of the Apps list, as English and Spanish do for languages.
  const myToolKeys = new Set(myTools.map(toolKey));
  const pinnedTools = toolOptions.filter((o) => myToolKeys.has(toolKey(o.value))).map((o) => o.value);
  // The second sentence only when onboarding gave them apps, which Browse ticks on arrival.
  const appsHelper = `What apps should you be familiar with?${myTools.length ? " We’ve already selected the ones you use." : ""}`;

  useEffect(() => {
    if (!seedingTools) return undefined;
    let live = true;
    fetchCompanyTools().then((tools) => {
      if (!live) return;
      const mine = uniqueTools(tools);
      if (!mine.length) {
        setSeedingTools(false);
        return;
      }
      toolsSeeded.current = true;
      setToolFilters(mine);
    });
    return () => {
      live = false;
    };
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The seeded apps are in place: let the list load (and filter tracking count later changes again).
  useEffect(() => {
    if (seedingTools && toolsSeeded.current) setSeedingTools(false);
  }, [seedingTools, toolFilters]);

  // Load talents when filters or pagination change
  useEffect(() => {
    if (seedingTools) return;
    loadTalents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilters, toolFilters, languageFilters, currentPage, itemsPerPage, seedingTools]);

  const listState = () => ({ roles: roleFilters, tools: toolFilters, languages: languageFilters, page: currentPage, perPage: itemsPerPage, scrollY: 0 });

  // A save from this list (or from a profile opened from it) keeps the filters it was made under.
  useEffect(() => {
    setListFilters({ roles: roleFilters, tools: toolFilters, languages: languageFilters });
  }, [roleFilters, toolFilters, languageFilters]);
  useEffect(() => () => setListFilters(null), []);

  // Kept as it changes, so the list is the same when the customer comes back to it.
  useEffect(() => {
    saveListState(listState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilters, toolFilters, languageFilters, currentPage, itemsPerPage]);

  // Back from a profile: once the list is drawn again, return to where the customer was.
  useEffect(() => {
    if (isLoading || !restoreScroll.current) return;
    const y = restoreScroll.current;
    restoreScroll.current = 0;
    window.scrollTo(0, y);
  }, [isLoading]);

  const loadTalents = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const response = await api.fetchTalentList({
        page: currentPage,
        limit: itemsPerPage,
        language: languageFilters.length > 0 ? languageFilters : undefined,
        tool: toolFilters.length > 0 ? toolFilters : undefined,
        // The Role filter lists arbiter roles, so the picks filter on the arbiter-assigned role.
        arbiterRole: roleFilters.length > 0 ? roleFilters : undefined,
        customerId: loggedInUser?.customerId || undefined,
      });
      setTalents(response.talentRecords);
      setPagination(response.pagination);
    } catch {
      setHasError(true);
      setTalents([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
    }
  };

  const nudgeAfter = hireNudgeAfter({
    positions: nudgePositions,
    page: currentPage,
    limit: itemsPerPage,
    shown: talents.length,
    total: pagination?.total ?? talents.length,
  });

  const roleNote = meeting ? (
    <>
      Cannot find the role you need?{" "}
      <button
        type="button"
        className="talent-network__role-meeting"
        onClick={() => startHireRequest("role_filter")}
        disabled={meeting.sent}
      >
        {meeting.sent ? "Meeting requested" : "Request a meeting"}
      </button>
    </>
  ) : canRequestHire ? (
    <>
      Cannot find the role you need?{" "}
      <Link to={api.routes.requestAHire} onClick={() => analytics.track("Hire Request Started", { context: "role_filter" })}>
        Request a hire
      </Link>
    </>
  ) : undefined;

  const activeFilterCount = roleFilters.length + toolFilters.length + languageFilters.length;

  const applySheet = (next: Record<string, string[]>) => {
    handleApplyRoles(next.role || []); // also retires the role hint
    setToolFilters(next.tool || []);
    setLanguageFilters(next.language || []);
  };

  const startHireRequest = (context: string) => {
    analytics.track("Hire Request Started", { context });
    if (meeting) meeting.open(context);
    else navigate(api.routes.requestAHire);
  };

  const handleTalentClick = (talentId: string) => {
    if (api.kind === "plg") {
      // eslint-disable-next-line no-underscore-dangle
      const index = talents.findIndex((t) => t._id === talentId);
      // The profile opens in its own tab; it reads this once for profile_viewed.
      if (index >= 0) rememberListContext(talentId, { position: (currentPage - 1) * itemsPerPage + index + 1, role: talents[index].roles?.[0] });
    }
    rememberOpenedFilters(talentId);
    // In place, like the prototype (QA PO-8); Back returns to this list as it is now.
    saveListState({ ...listState(), scrollY: window.scrollY });
    navigate(api.routes.talentProfile(talentId));
  };

  const handlePageChange = (newPage: number) => {
    if (pagination && newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleItemsPerPageChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  // New filters start from the first result, at the top of the list (QA PO-12).
  const toFirstPage = () => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleApplyRoles = (values: string[]) => {
    setRoleFilters(values);
    // The role hint has done its job once a role is picked (prototype state.roleTaught).
    if (values.length) {
      setRoleTaught(true);
      try {
        sessionStorage.setItem(ROLE_TAUGHT_KEY, "1");
      } catch {
        // storage unavailable: the hint just reappears next visit
      }
    }
    toFirstPage();
  };

  const handleApplyTools = (values: string[]) => {
    setToolFilters(values);
    toFirstPage();
  };

  const handleApplyLanguages = (values: string[]) => {
    setLanguageFilters(values);
    toFirstPage();
  };

  // Prototype tn-meta: one "Clear filters" for every filter, next to the result count.
  const clearFilters = () => {
    setRoleFilters([]);
    setToolFilters([]);
    setLanguageFilters([]);
    toFirstPage();
  };

  const handleSearchClick = () => {
    setCurrentPage(1);
    loadTalents();
  };

  const handleRequestInterview = useCallback(
    async (talentId: string) => {
      if (!loggedInUser) return;
      // eslint-disable-next-line no-underscore-dangle
      const talent = talents.find((t) => t._id === talentId);
      if (!talent) return;

      setRequestingTalentId(talentId);
      try {
        await api.requestInterview({
          customerId: loggedInUser.customerId,
          customerUserName: loggedInUser.name || "",
          customerUserEmail: loggedInUser.email || "",
          customerUserId: loggedInUser.userId || "",
          talentId,
          talentName: getTalentFullName(talent),
          talentProfileUrl: `${window.location.origin}${api.routes.talentProfile(talentId)}`,
          requestedAt: new Date().toLocaleString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
        });
        // Mark as requested locally
        // eslint-disable-next-line no-underscore-dangle
        setTalents((prev) => prev.map((t) => (t._id === talentId ? { ...t, interviewRequested: true } : t)));
        analytics.track("Talent Interview Requested", {
          talentId,
          talentName: `${talent.firstName} ${talent.lastName}`.trim(),
        });
        setShowSuccessModal(true);
      } catch {
        // silently fail — could add toast later
      } finally {
        setRequestingTalentId(null);
      }
    },
    [loggedInUser, talents, api]
  );

  const getTotalRangeText = () => {
    if (!pagination || pagination.total === 0) return "0-0 of 0";
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total);
    return `${start}-${end} of ${pagination.total}`;
  };

  if (!loadingUserInfo && !talentNetworkVisibility) {
    return (
      <div className="talent-network-page">
        <div className="talent-network__header">
          <div className="talent-network__title heading-h2-semibold color-text-headings-primary">
            You are not allowed to access this page
          </div>
        </div>
      </div>
    );
  }

  // PLG calls this page Browse in the sidebar, breadcrumb and browser tab; the heading says Talent Network for everyone.
  const pageTitle = "Talent Network";
  const tabTitle = api.kind === "plg" ? "Browse" : pageTitle;

  return (
    <div className="talent-network-page">
      <Helmet>
        <title>{`Edge | ${tabTitle}`}</title>
      </Helmet>
      {isLoading && <PageLoader />}
      <div className="talent-network__header">
        <div className="talent-network__header-ellipse talent-network__header-ellipse--pink" />
        <div className="talent-network__header-ellipse talent-network__header-ellipse--blue" />
        <div className="talent-network__header-content">
          <div className="talent-network__header-left">
            <div className="talent-network__title heading-h2-semibold color-text-headings-primary">{pageTitle}</div>
            <div className="talent-network__subtitle para-body-m-regular color-text-headings-secondary">
              {/* "top-tier" never breaks at its hyphen */}
              Discover your next <span style={{ whiteSpace: "nowrap" }}>top-tier</span> hire
            </div>
          </div>
          {meeting ? (
            // Prototype .tn-ask: the whole card opens the meeting panel.
            <button
              type="button"
              className="talent-network__ask"
              data-testid="tn-ask"
              onClick={() => startHireRequest("header")}
              disabled={meeting.sent}
            >
              {shortlisted > 0 && !meeting.sent ? (
                <span className="talent-network__ask-text">
                  <b>{`${shortlisted} shortlisted`}</b>
                  <span>We will use these to understand what you are looking for.</span>
                </span>
              ) : (
                <span className="talent-network__ask-text">
                  <b>Have something specific in mind?</b>
                  <span>We source any role, any industry.</span>
                </span>
              )}
              <span className="talent-network__ask-link">
                {meeting.sent ? "Meeting requested" : "Request a meeting"}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </span>
            </button>
          ) : canRequestHire && (
            <div className="talent-network__header-cta">
              <div className="talent-network__cta-text">
                <span className="talent-network__cta-title">Have something specific in mind?</span>
                <span className="talent-network__cta-desc">We source any role, any industry.</span>
              </div>
              <Link
                to={api.routes.requestAHire}
                className="talent-network__cta-link"
                onClick={() => analytics.track("Hire Request Started", { context: "header" })}
              >
                Request a hire
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 12L10 8L6 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>
          )}
        </div>
      </div>
      <div className="talent-network">
        <button type="button" className="talent-network__m-filters" onClick={() => setFilterSheetOpen(true)}>
          Filters
          {activeFilterCount > 0 && <span className="talent-network__m-filters-badge">{activeFilterCount}</span>}
        </button>
        {filterSheetOpen && (
          <MobileFilterSheet
            sections={[
              { key: "role", label: "Role", helper: ROLE_HELPER, options: roleOptions, note: roleNote, single: true },
              { key: "tool", label: "Apps", helper: appsHelper, options: toolOptions, pinned: pinnedTools },
              {
                key: "language",
                label: "Languages",
                helper: LANGUAGES_HELPER,
                options: [...TOP_LANGUAGES, ...ALL_LANGUAGES],
                pinned: TOP_LANGUAGES.map((l) => l.value),
              },
            ]}
            selected={{ role: roleFilters, tool: toolFilters, language: languageFilters }}
            onApply={applySheet}
            onClose={() => setFilterSheetOpen(false)}
          />
        )}
        <div className="filter-bar" data-testid="filter-bar">
          <div className="filter-bar__sections">
            <FilterDropdown
              label="Role"
              title="Role"
              helper={ROLE_HELPER}
              mode="single"
              selectedValues={roleFilters}
              onApply={handleApplyRoles}
              options={roleOptions}
              searchable={false}
              wide
              note={roleNote}
            />
            <FilterDropdown
              label="Apps"
              title="Apps"
              helper={appsHelper}
              mode="checkbox"
              selectedValues={toolFilters}
              onApply={handleApplyTools}
              options={toolOptions}
              pinned={pinnedTools}
              searchable
              emptyText="No apps match that name"
            />
            <FilterDropdown
              label="Languages"
              title="Languages"
              helper={LANGUAGES_HELPER}
              mode="checkbox"
              selectedValues={languageFilters}
              onApply={handleApplyLanguages}
              topOptions={TOP_LANGUAGES}
              options={ALL_LANGUAGES}
              searchable
              emptyText="No languages match that name"
            />
          </div>
          <button type="button" className="filter-bar__search-btn" onClick={handleSearchClick} aria-label="Search">
            <SearchIcon sx={{ fontSize: 24 }} />
          </button>
        </div>

        {!roleTaught && roleFilters.length === 0 && (
          <div className="talent-network__role-hint">
            <PlgIcon name="help" />
            <div>
              <b>Not sure which role you need?</b>
              Open the role filter. Each one explains what that person does day to day.
            </div>
          </div>
        )}

        {!isLoading && pagination && (pagination.total > 0 || activeFilterCount > 0) && (
          <div className="talent-network__results-count para-body-m-regular color-text-heading-secondary">
            <span>
              Showing <strong>{pagination.total}</strong> talent {pagination.total === 1 ? "profile" : "profiles"}
            </span>
            {activeFilterCount > 0 && (
              <button type="button" className="talent-network__clear-filters" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        )}

        <div className="talent-network__cards">
          {hasError ? (
            <div className="talent-network__error">
              <WarningAmberIcon sx={{ fontSize: 32, color: "#AFAFAF" }} />
              <div className="talent-network__error-text">
                <div className="talent-network__error-title">Something went wrong</div>
                <div className="talent-network__error-subtitle">
                  We couldn&apos;t load the profiles right now. Please try reloading.
                </div>
              </div>
              <button type="button" className="talent-network__error-cta" onClick={loadTalents}>
                <RefreshIcon sx={{ fontSize: 18, color: "#EDEDED" }} />
                Reload
              </button>
            </div>
          ) : (
            <>
              {/* eslint-disable no-underscore-dangle */}
              {Array.isArray(talents) && talents.length > 0 ? (
                talents.map((talent, i) => (
                  <Fragment key={talent._id}>
                    <TalentCard
                      talent={talent}
                      onClick={() => handleTalentClick(talent._id)}
                      onRequestInterview={shortlistMode ? undefined : handleRequestInterview}
                      isRequesting={requestingTalentId === talent._id}
                      shortlistMode={shortlistMode}
                      // A match is an app the customer is filtering on (their own apps by default).
                      myTools={toolFilters}
                    />
                    {/* PLG: "Not finding the right person?" is hidden for now (product 2026-11-04); portal customers keep
                        Request a hire. To bring it back for PLG, change the condition back to (meeting || canRequestHire). */}
                    {!meeting && canRequestHire && nudgeAfter.has(i) && (
                      <HireNudgeRow
                        title="Not finding the right person?"
                        description={`${meeting ? "Request a meeting" : "Request a hire"} and our hiring experts will come back to you with people who fit.`}
                        onRequestHire={() => startHireRequest("list_nudge")}
                        meeting={!!meeting}
                        sent={!!meeting?.sent}
                      />
                    )}
                  </Fragment>
                ))
              ) : (
                /* eslint-enable no-underscore-dangle */
                meeting || canRequestHire ? (
                  <HireNudgeRow
                    title="No one matches that combination yet"
                    description={
                      meeting
                        ? "Request a meeting and our hiring experts will find them."
                        : "Tell us what you need and our hiring experts will find them."
                    }
                    onRequestHire={() => startHireRequest("no_results")}
                    meeting={!!meeting}
                    sent={!!meeting?.sent}
                  />
                ) : (
                  <div className="talent-network__empty">
                    <PersonSearchOutlinedIcon sx={{ fontSize: 36, color: "#AFAFAF" }} />
                    <div className="talent-network__empty-text">
                      <div className="talent-network__empty-title">No one matches that combination yet</div>
                    </div>
                  </div>
                )
              )}
            </>
          )}
        </div>

        {pagination && pagination.total > 0 && (
          <div className="talent-network__pagination">
            <div className="talent-network__pagination-info">
              <span className=" para-body-m-regular color-text-body-secondary">Profiles per page:</span>
              <select
                className="talent-network__pagination-select"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
              <span className="para-body-m-regular color-text-body-secondary">{getTotalRangeText()}</span>
            </div>

            <div className="talent-network__pagination-controls">
              <button
                className="talent-network__pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                type="button"
                aria-label="Previous page"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M12.5 15L7.5 10L12.5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <button
                className="talent-network__pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                type="button"
                aria-label="Next page"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M7.5 15L12.5 10L7.5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
      <InterviewSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        showStayHere={false}
        onViewOtherProfiles={() => setShowSuccessModal(false)}
      />
    </div>
  );
}

export default TalentNetworkPage;
